import { authApi, investorApi } from '@core/api/httpClient'
import { ONBOARDING_ENDPOINTS } from './onboardingEndpointPaths'
import type { RegisterPayloadDto, RegisterResponseDto } from './contracts/signUpContracts'
import type {
  CurrentStepsResponseDto,
  StartOnboardingPayload,
  StartOnboardingResponseDto,
} from './contracts/onboardingContracts'
import type {
  RiskProfileResponseDto,
  SubmitRiskProfilePayload,
  SubmitRiskProfileResponseDto,
} from './contracts/riskProfileContracts'
import type { SubmitSofPayload, SubmitSofResponseDto } from './contracts/sourceOfFundsContracts'
import type { KycSessionResponseDto, KycStartSessionParams } from './contracts/kycContracts'
import type { CountriesResponseDto } from './contracts/countriesContracts'
import type { RegisterInput } from '../types/signUpTypes'

// signUp ----------------------------------------------------------------------

export async function register(input: RegisterInput): Promise<RegisterResponseDto> {
  const payload: RegisterPayloadDto = {
    portalCode: input.portalCode,
    firstName: input.firstName,
    lastName: input.lastName,
    email: input.email,
    mobileCountryCode: input.mobileCountryCode,
    mobile: input.mobile,
  }
  const { data } = await authApi.post<RegisterResponseDto>(ONBOARDING_ENDPOINTS.REGISTER, payload)
  return data
}

// onboarding (flow start + resume) -------------------------------------------

export async function startOnboarding(
  payload: StartOnboardingPayload,
): Promise<StartOnboardingResponseDto> {
  const { data } = await investorApi.post<StartOnboardingResponseDto>(
    ONBOARDING_ENDPOINTS.ONBOARDING_START,
    payload,
  )
  return data
}

export async function getCurrentSteps(): Promise<CurrentStepsResponseDto> {
  const { data } = await investorApi.get<CurrentStepsResponseDto>(
    ONBOARDING_ENDPOINTS.ONBOARDING_STEPS,
  )
  return data
}

export async function completeOnboardingStep(stepKey: string): Promise<void> {
  const url = ONBOARDING_ENDPOINTS.ONBOARDING_STEP_COMPLETE.replace('{step_key}', stepKey)
  await investorApi.post<void>(url)
}

// Marks an optional (skippable) step SKIPPED server-side. The step stays
// completable later through its real submission flow — e.g. risk profiling
// from the dashboard entry point.
export async function skipOnboardingStep(stepKey: string): Promise<void> {
  const url = ONBOARDING_ENDPOINTS.ONBOARDING_STEP_SKIP.replace('{step_key}', stepKey)
  await investorApi.post<void>(url)
}

export async function completeOnboarding(): Promise<void> {
  await investorApi.post<void>(ONBOARDING_ENDPOINTS.ONBOARDING_COMPLETE)
}

// riskProfile -----------------------------------------------------------------

export async function getRiskProfile(): Promise<RiskProfileResponseDto> {
  const { data } = await investorApi.get<RiskProfileResponseDto>(ONBOARDING_ENDPOINTS.RISK_PROFILE)
  return data
}

export async function submitRiskProfile(
  payload: SubmitRiskProfilePayload,
): Promise<SubmitRiskProfileResponseDto> {
  const { data } = await investorApi.post<SubmitRiskProfileResponseDto>(
    ONBOARDING_ENDPOINTS.RISK_PROFILE,
    payload,
  )
  return data
}

// sourceOfFunds ---------------------------------------------------------------

export async function submitSourceOfFunds(
  payload: SubmitSofPayload,
): Promise<SubmitSofResponseDto> {
  const { data } = await investorApi.post<SubmitSofResponseDto>(
    ONBOARDING_ENDPOINTS.SOURCE_OF_FUNDS,
    payload,
  )
  return data
}

// kyc -------------------------------------------------------------------------

export async function createKycSession(): Promise<KycSessionResponseDto> {
  const { data } = await investorApi.post<KycSessionResponseDto>(ONBOARDING_ENDPOINTS.KYC_SESSIONS)
  return data
}

export async function getKycAccessToken(): Promise<string> {
  const { data } = await investorApi.get<{ token: string }>(ONBOARDING_ENDPOINTS.KYC_ACCESS_TOKEN)
  return data.token
}

export async function startKycSession(params: KycStartSessionParams): Promise<void> {
  const url = ONBOARDING_ENDPOINTS.KYC_SESSION_START.replace('{submission_id}', params.submissionId)
  await investorApi.post<void>(url)
}

// Signals the backend that the investor has finished submitting their KYC, so
// it marks the submission as awaiting review and moves the investor to
// pending_review. The re-KYC resubmit flow relies on this to enter the admin
// onboarding-review queue (it skips /onboarding/complete, the happy-flow path
// that otherwise sets pending_review).
export async function completeKycSession(params: KycStartSessionParams): Promise<void> {
  const url = ONBOARDING_ENDPOINTS.KYC_SESSION_COMPLETE.replace(
    '{submission_id}',
    params.submissionId,
  )
  await investorApi.post<void>(url)
}

// countries (consumed by sourceOfFunds) ---------------------------------------

export async function getCountries(): Promise<CountriesResponseDto> {
  const { data } = await investorApi.get<CountriesResponseDto>(ONBOARDING_ENDPOINTS.COUNTRIES)
  return data
}
